import { preview } from './preview.js';
import type { ErrorBody } from '@werft/contracts';

/** Fehler nach dem einheitlichen Vertrag des Servers (Leitlinie, Seite 6) */
export class ApiError extends Error {
  constructor(
    public status: number,
    public body: ErrorBody,
  ) {
    super(body.message);
  }
  get code() {
    return this.body.code;
  }
}

async function request<T>(method: string, url: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {}, signal };
  // P21: Vorschau „Als Gast ansehen“ – der Server zeigt die Sicht des Gastes
  const p = preview();
  if (p) (init.headers as Record<string, string>)['x-werft-als'] = p.userId;
  if (body !== undefined) {
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    // 0.158 (ADR 0164): abgebrochener Leseabruf ist kein Fehler – TanStack Query verwirft ihn still
    if (signal?.aborted) throw e;
    // Netzfehler: Ausgang unbekannt – Wiederholung mit derselben opId ist sicher
    throw new ApiError(0, { code: 'internal', message: 'Keine Verbindung zum Server.' });
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    const b = (data && typeof data === 'object' && 'code' in data ? data : { code: 'internal', message: `Fehler ${res.status}` }) as ErrorBody;
    throw new ApiError(res.status, b);
  }
  return data as T;
}

export const api = {
  /** Leseabruf; `signal` (aus dem queryFn-Kontext) bricht ihn ab, wenn die Ansicht ihn nicht mehr braucht. Nur Lesen – Schreibwege nie abbrechen. */
  get: <T>(url: string, opts?: { signal?: AbortSignal }) => request<T>('GET', url, undefined, opts?.signal),
  post: <T>(url: string, body: unknown = {}) => request<T>('POST', url, body),
  patch: <T>(url: string, body: unknown) => request<T>('PATCH', url, body),
  put: <T>(url: string, body: unknown) => request<T>('PUT', url, body),
  del: <T>(url: string, body?: unknown) => request<T>('DELETE', url, body),
};

/** opId je Änderung; crypto.randomUUID gibt es nur in sicheren Kontexten (https/localhost) */
export function newOpId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID().replace(/-/g, '');
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (x) => x.toString(16).padStart(2, '0')).join('');
}

/** UUID (v4) für Datensätze, die der Browser anlegt (Seiten, Seiten-Kommentare) – auch ohne sicheren Kontext */
export function newUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
